import { Slide } from '../../components/SlideComponents/slide.types';
import { AiAssessedScore } from '../../frontendServices/gameAPI';
import { AndString } from '../../utils/and-string';
import { AiConsumerContent, AiContent, TemplateSettings } from '../TemplateService/Template.type';
import { LessonAttachment } from './backendService';

export type UserTenant = {
  activeTenantId: string;
  availableTenantIds: string[];
  availableTenants: { tenantId: string; tenantName: string }[];
};

export type LessonContext = {
  grade: string;
  subject: string;
  learningObjective: string;
  language: string;
}

export type Maybe<T> = T | undefined | null;

export type ById<T> = {
  [id: string]: T;
};

// copied from api
export type ProcessDocumentErrorMessage = 'Failed to process document' | 'Failed to find content in parsed file' | 'Unknown failure reason';

export type Round = {
  id: string;
  type: RoundType;
  title?: string | null;
  durationSeconds?: number | null; // transactional
  durationSecondsConfig?: number | null; // configurable
  wasPlayed?: boolean | null; // transactional
  dataReferenceRoundId?: string | null;
  media?: RoundMedia | null;
  content?: SlideContent | null;
  metadata?: ById<string | boolean | number> | null;
  roundOptions?: RoundOptions[] | null;
  responseLimit?: number | null;
  responseCharacterLimit?: number | null;
  shuffleOptions?: boolean | null;
  wasAddedDuringLive?: boolean | null;
  backgroundMedia?: RoundMedia | null; // Place a media as background
};

export type RoundType =
  | 'questions' /** @deprecated */
  | 'voting'
  | 'answers'
  | 'aiAssessedAnswers'
  | 'aiAssessedImageAnswers'
  | 'ideas'
  | 'slide'
  | 'canvas'
  | 'responsegrid'
  | 'wordcloud'
  | 'poll'
  | 'multipleChoice'
  | 'videos' /** @deprecated */
  | 'groupmaker' /** @deprecated */
  | 'template variant'
  | 'podium';

export type SlideType =
  | 'header'
  | 'header_text'
  | 'bullets'
  | 'image'
  | 'text_image'
  | 'image_text'
  | 'fullscreen_media';
export type SlideBullet = {
  id: string;
  text: string;
};
export type RoundOptions = {
  id: string;
  displayName: string;
  count?: number;
  color?: string;
  isCorrect?: boolean;
  explanation?: string;
};

export type CustomSlideTextSize = 'XS' | 'S' | 'M' | 'L' | 'XL' | '2XL' | '3XL';

export type SlideContent = {
  type: SlideType;
  title: string | null /** @depricated due to custom slides using round.title*/;
  subtitle: string | null;
  bullets?: SlideBullet[] | null /** @depricated due to custom slides */;
};

export type RoundMedia = {
  fileId: string;
  thumbnailSrc?: string | null;
  fileMimeType: string;
  fileAltText?: string | null;
  downloadLocation?: string | null;
  cropConfig?: CropConfig | null;
  backgroundDrawingFileId?: string | null;
  brightness?: number | null;
  backgroundDrawingConfigFiles?: RoundMediaDrawingFile[] | null;
  width?: number;
  height?: number;
};

export type RoundMediaDrawingFile = {
  id: string;
  created: number;
  mimeType: string;
};

export type CropConfig = {
  height: number;
  width: number;
  naturalHeight: number;
  naturalWidth: number;
  x: number;
  y: number;
};

export type LookupTenant = { tenantId: string; tenantName: string };

export type Invitation = {
  code: string; // The code used to verify. Will also be its Id
  tenantId: string; // The tenantId this code belongs to
  expiresAt: number | null; // If null means it will never expire
  createdBy: string;
  organizationRole: InvitationRole;
  email?: string | null;
};

export type InvitationRole = Omit<UserRole, 'Admin'>;

export type EmailInvitationCreateDTO = {
  email: string;
  role: InvitationRole;
};

export type RoundResult = {
  questions?: Response[];
  responses?: Response[];
  id: string;
  votes?: Vote[];
};

export type Vote = {
  id: string;
  selectedQuestionId: string;
  selectedResponseId?: string;
  questionAId: string;
  questionBId: string;
  responseAId?: string;
  responseBId?: string;
  votedBy: string;
};

export type Participant = {
  id: string;
  gameId: string;
  tenantId: string;
  nickName: string;
  token: string; // the token passed with all api requests from the anonymous user. Generated in the monolith
  authToken: string;
  name?: string;
  isKicked?: boolean;
  joinedAt?: number;
};

export type PartialParticipant = Pick<
  Participant,
  'id' | 'nickName' | 'name' | 'isKicked'
>;

export type GamePinRegistration = {
  pin: string;
  tenantId: string;
  createdAt: number;
  gameId: string;
};

export type Game = {
  id: string;
  topicId?: string | null;
  pin: string | null;
  title?: string | null;
  isPlaying: boolean;
  isComplete: boolean;
  isPreviewGame: boolean;
  isFromDiscover?: boolean | null;
  isDemoGame?: boolean | null;
  templateId?: string | null;
  areAllRoundsCompleted?: boolean | null;
  metadata?: ById<string | boolean | number>;
  settings?: TemplateSettings | null;
  roundStartsAt: number | null;
  roundFreezedAt: number | null;
  currentRound: string | null;
  lastPlayedAt: number | null;
  isShowingLobby?: boolean | null;
  createdAt: number | null;
  modifiedAt: number | null;
  language?: string | null;
  activity?: GameActivity;
  host: {
    uid: string;
    username?: string;
  };
  rounds: Round[] | null;
  slides: Slide[] | null;
  attachments?: LessonAttachment[];
  roundResults?: RoundResult[]; // included when complete
  participants?: Participant[]; // included when complete
  requireRealNames?: boolean;
  isContentFilterEnabled?: boolean;
  assignedClassId?: string | null;
};

export type TeacherClass = {
  id: string;
  name: string;
  createdAt?: number | null;
  studentCount: number;
};

export type ClassStudent = {
  id: string;
  name: string;
};

export type GameSaveDTO = {
  id: string;
  tenantId?: string | null;
  title: string | null;
  pin: string | null;
  isPreviewGame: boolean;
  isFromDiscover: boolean | null;
  templateId: string | null;
  language: string | null;
  isPlaying: boolean;
  isComplete: boolean;
  areAllRoundsCompleted: boolean;
  requireRealNames: boolean;
  isContentFilterEnabled?: boolean;
  roundStartsAt: number | null;
  roundFreezedAt: number | null;
  currentRound: string | null;
  isShowingLobby: boolean | null;
  lastPlayedAt: number | null;
  createdAt: number;
  metadata: ById<string | number | boolean> | null;
  settings?: TemplateSettings | null;
  modifiedAt: number;
  host: {
    uid: string;
  };
  rounds: Round[];
  slides: Slide[];
  attachments?: LessonAttachment[];
  assignedClassId?: string | null;
};

export type PaginatedGameResults = {
  items: Game[];
  cursor?: number;
  count: number;
};

export type Score = {
  score: number;
  questionId: string;
  question: Response;
  responseId: string;
  response: Response;
  feedback?: AiAssessedScore;
};

export type Response = {
  id: string;
  question: string;
  response: string;
  uid: string;
  timestamp?: number;
  modifiedAt?: number;
  isFinished?: boolean;
  dismissed?: boolean;
  strength?: { description: string; strengthText: string } | null;
  drawingDetails?: number | null;
  isModerated?: boolean;
};

export type LeaderBoard = Score[];

type GameActivity = 'kickstart' | 'summarize' | 'brainstorm' | 'answer';

type UserRole = 'Member' | 'Admin';

const logos = [
  ':curipod_stars:',
  ':curipod_thanks:',
  ':curipod_hearts:',
  ':curipod_normal:',
] as const;

export type LogoType = typeof logos[number];

export type ProfileLogoConfig = {
  color: string;
  logo: LogoType;
};

export type MSClaims = {
  admin: boolean;
  mentor: boolean;
  student: boolean;
};

export type AvailableFilterDTO = {
  key: string;
  doc_count: number;
};

export type GameEditOrCreateDTO = {
  settings?: TemplateSettings | null;
  rounds?: Round[];
  slides?: Slide[];
  attachments?: LessonAttachment[];
  topicId?: string | null;
  title?: string | null;
  gameId?: string | null;
  currentRound?: string | null;
  modifiedAt?: number | null;
  roundStartsAt?: number | null;
  isPlaying?: boolean; // @deprecated, this shoudl be removed when we're at flexible lobby
  lastPlayedAt?: number | null;
  gameIds?: string[];
  requireRealNames?: boolean;
  isContentFilterEnabled?: boolean;
  isShowingLobby?: boolean;
  assignedClassId?: string | null;
};

export type ActivityResults = {
  totalParticipants: number;
  activeParticipants: number;
  participants: ById<Participant>;
  slidesResults: ActivitySlideResult[];
};

export type ActivitySlideResult = {
  slides: Slide[];
  scores: Score[];
  wasPlayed: boolean;
  activeParticipants: number;
};
export type ActivityRoundResult = {
  rounds: Round[];
  scores: Score[];
  wasPlayed: boolean;
  activeParticipants: number;
};

export type DiscoveredTemplateStatus =
  | 'published'
  | 'rejected'
  | 'submitted'
  | 'unPublishedByOwner'
  | 'staging';

export type DiscoveredTemplate = {
  id: string;
  tenantId: string;
  title: string;
  description: string | null;
  lastPublished: number | null;
  firstPublished: number | null;
  createdAt: number;
  modifiedAt: number;
  creatorId: string;
  creatorProfileId: string;
  creatorThumbnailId?: string | null;
  rounds: Round[]; // @deprecated due to slides
  slides: Slide[];
  language: string | null;
  coverImageId: string | null;
  coverImageAltText: string | null;
  downloadCount: number;
  playCount: number;
  participantCount?: number;
  tags: string[];
  grades: string[];
  creatorName: string;
  status: DiscoveredTemplateStatus;
  requireRealNames?: boolean;
  adminTags?: string[];
  adminLanguage?: string;
  roundIndexField?: string;
  aiConsumerContent: AiConsumerContent;
  slideIndexField?: string;
  aiContent?: AiContent;
  learningStandards?: Standards[];
  settings?: TemplateSettings | null;
};

export type IndexedTemplate = Omit<DiscoveredTemplate, 'status'>;

export type TemplateDataType = {
  id: string;
  tenantId: string;
  type: 'template';
  parentId: string | null;
  title?: string | null;
  description?: string | null;
  lastPublished?: number;
  isPrivate: boolean;
  coverImageId?: string | null;
  coverImageAltText?: string | null;
  clonedFromTemplateId?: string | null;
  templateSharingPublic?: boolean | null;
  isBlockedFromSharing?: boolean | null;
  createdAt: number;
  requireRealNames?: boolean;
  isContentFilterEnabled?: boolean;
  modifiedAt: number;
  topicId?: string | null;
  isDeleted?: boolean | null;
  currentRound?: string | null;
  deletedAt?: number | null;
  language?: string | null; // en, nb-NO, de
  creator: {
    uid: string;
  };
  members: string[];
  content: TemplateContent;
  // This is data storage only - will be hidden from clients
  workspaceActiveGame?: WorkspaceActiveGame;
  metadata?: ById<string | boolean | number> | null;
};

export type TemplateContent = {
  rounds: Round[] | null;
  gameIds: string[];
  activeGame?: ActiveGame;
};

export type WorkspaceActiveGame = ById<ActiveGame>;

export type ActiveGame = {
  id: string;
  isOngoing: boolean;
} | null;

export type FeedbackType = 'writingFeedback' | 'comprehensionFeedback';

export type PromptMessageRole = 'user' | 'system' | 'assistant';

export type PromptDependency = {
  dependencyId: string;
  type: 'formInput' | 'promptOutput' | 'globalPromptMiddleware';
};

export type LocalPromptDependency = {
  id: string;
  type: 'input' | 'output' | 'live';
  label: string;
  formType: PromptInputType;
  defaultValue?: string;
  constraints?: PromptInputConstraints;
};

export const reasoningEffortOptions = ['none', 'minimal', 'low', 'medium', 'high'] as const;
export type ReasoningEffort = (typeof reasoningEffortOptions)[number];

export type Prompt = {
  id: string;

  name: string;
  // The specific string like `create a funny drawing activity based on {event}`
  messages: { role: PromptMessageRole; content: string | undefined }[];

  promptOutput: PromptOutput;
  temperature?: number;
  reasoningEffort?: ReasoningEffort;

  negativePrompt?: string;
  variantId?: string;
  // Per-variant max score override for AI-assessed activities. Only set on a
  // variant prompt (variantId present). undefined = inherit the slide's
  // maxScore, null = explicit "None", number = literal max. Propagated onto the
  // generated slide's maxScore by the backend at generation time.
  maxScore?: number | null;
  // prompt execution
};

export const AIResourceTypes = {
  openai: 'openAi',
  openaiImage: 'openAi-image',
  openAiMultiModal: 'openAi-multiModal',
  stableDiffusionImage: 'stable-diffusion-image',
  ideogramImage: 'ideogram-image',
  nanoBananaProImage: 'nano-banana-pro-image',
  nanoBanana2Image: 'nano-banana-2-image',
} as const;

export type ResourceTypeKeys = keyof typeof AIResourceTypes;
export type ResourceType = typeof AIResourceTypes[ResourceTypeKeys];

export const promptModels = {
  default: 'default',
  // Legacy models (Stable Diffusion)
  sdCore: 'sd-core',
  sdUltra: 'sd-ultra',
  sd3Turbo: 'sd3-large-turbo',
  sd3: 'sd3-large',
  // Supported models (Stable Diffusion)
  sd3Medium: 'sd3-medium',
  sd35Medium: 'sd3.5-medium',
  sd35Large: 'sd3.5-large',
  sd35LargeTurbo: 'sd3.5-large-turbo',
  nanoBananaPro: 'google/nano-banana-pro',
  nanoBanana2: 'google/nano-banana-2',
  ideogramV3Turbo: 'ideogram-v3-turbo',
  // Supported models (OpenAI)
  gpt35Turbo: 'gpt-3.5-turbo', // deprecated
  gpt4: 'gpt-4', // deprecated
  gpt4o: 'gpt-4o',
  gpt4omini: 'gpt-4o-mini',
  gpt4Turbo: 'gpt-4-turbo', // deprecated
  gpt5: 'gpt-5',
  gpt5Mini: 'gpt-5-mini',
  gpt5Nano: 'gpt-5-nano',
  gpt52: 'gpt-5.2',
  gpt41: 'gpt-4.1',
  gpt41Mini: 'gpt-4.1-mini',
  gpt41Nano: 'gpt-4.1-nano',
  o3: 'o3',
  dalle3: 'dall-e-3',
  gpt4Vision: 'gpt-4-vision-preview', // deprecated
} as const;

export type PromptModelKeys = keyof typeof promptModels;
export type PromptModels = typeof promptModels[PromptModelKeys];

export type PromptResource = {
  type: ResourceType;
  model: PromptModels;
  imageOrientation?: 'landscape' | 'portrait' | 'square';
};

export type PromptItemProperty = {
  type: 'string' | 'number';
  id: string; // `${promptId}.${property.id}.${itemIndex}.${itemValue.id}`
  description?: string;
};

export type PromptOutputPropertyItemType = 'string' | 'number' | 'object';

export type PromptOutputProperty = {
  type: 'string' | 'number' | 'array' | 'boolean';
  id: string;
  description?: string; // used by openai to match the prompt arguments
  // Used by array
  items?: {
    // used if array
    type: PromptOutputPropertyItemType;
    maxLength: number;
    properties?: {
      // used if object
      [name: string]: PromptItemProperty;
    };
    propertiesOrder?: string[];
  };
  // not used
  // enum?: string[];
};

export type PromptOutput = {
  type: 'string' | 'object' | 'array';
  resource: PromptResource;
  description: string;
  properties: {
    [name: string]: PromptOutputProperty;
  };
  propertiesOrder?: string[];
};

// the different of type maps out to different user input forms. upload is pdf/ppt

export const PromptInputTypesCore = {
  shortText: undefined,
  longText: undefined,
  dropdown: undefined,
  multiSelect: undefined,
  combobox: undefined,
  number: undefined,
  slidesUpload: 'pre',
  passage: 'pre',
} as const;

export type PromptInputType = keyof typeof PromptInputTypesCore;

export type PromptInput = {
  // customize
  id: string;
  type: PromptInputType;
  constraints?: PromptInputConstraints; //;
  order: number;
  value?: string;
  fileType?: string;
  label: string;
  description?: string;
  isRequired?: boolean;
  defaultValue?: string;
  allowEmpty?: boolean;
};
export type PromptInputData = Omit<
  PromptInput,
  'constraints' | 'order' | 'label' | 'description' | 'allowEmpty' | 'isRequired'
>;

export type PromptDropdownOption = {
  displayName: string;
  value: string;
  isEnterprise?: boolean; //Used for paywall
  disabled?: boolean;
};

export type PromptInputConstraints = {
  maxLength?: number;
  minLength?: number;
  max?: number;
  min?: number;
  step?: number;
  options?: PromptDropdownOption[];
  maxSelections?: number;
  // for upload
  allowedFileTypes?: string[];
  // for slider
};

export type PromptTestcase = {
  inputs: PromptInput[];
  id: string;
  creatorId: string;
  tenantId: string;
  modifiedAt: string;
  createdAt: string;
  isSuggestion: boolean;
  isSampleLesson: boolean;
};

export const FileUploadTypes = [
  'slideBackgroundImage',
  'slideImage',
  'generatorCoverImage',
  'generatorBackgroundImage',
  'profileImage',
  'passage',
  'materialAttachment'
] as const;

export type FileUploadType = typeof FileUploadTypes[number];

export type FileUploads = {
  id: string;
  description: string | null;
  fileId: string;
  mimeType: string;
  filePath: string;
  fileType: FileUploadType;
  fileOrientation: string | null;
  tenantId: string;
  uid: string;
  uploadCompleted: boolean;
  uploadCompletedAt: Date | null;
  createdAt: Date;
  llmToolCallId: string | null;
  llmMessageId: string | null
};

// AndString allows us to preserve autocomplete & IDE hints while keeping the type flexible
type MimeTypeType =
  | 'application'
  | 'audio'
  | 'image'
  | 'other'
  | 'text'
  | 'video'
  | AndString;
type MimeTypeSubType = 'jpeg' | 'png' | AndString;

// We want to be flexible on mimetypes while also giving an idea of what the types are supposed to look like
// NOTE: MimeType is a reserved word in TypeScript, so we use CPMimeType instead

// eslint-disable-next-line prettier/prettier
export type CPMimeType = `${MimeTypeType}/${MimeTypeSubType}`;

export type ImageMimeType = `image/${MimeTypeSubType}`;

export function isValidMimeType(mimeType: string): mimeType is CPMimeType {
  const parts = mimeType.split('/');
  return parts.length === 2 && !!parts[0].length && !!parts[1].length;
}

export type PromptSingleTestCaseResultType = ResourceType | PromptInputType;

export type PromptSingleTestCaseResult = ById<{
  type: PromptSingleTestCaseResultType;
  id: string;
  value: string;
}>;

export type PromptResourceMapValue = {
  id: string;
  value: string;
  type: ResourceType;
  mediaThumbnailUrl?: string;
  mediaDownloadLocation?: string;
};
export type PromptResourceMap = ById<PromptResourceMapValue>;

export type PresentationContent = {
  currentSlideId?: string;
  slides: Slide[];
  attachments?: LessonAttachment[];
  deprecatedRounds: Round[];
  title?: string;
  description?: string;
  lastPublished?: number;
  coverImageId?: string;
  coverImageAltText?: string;
  livePresentationIds: string[];
  templateSharingPublic: boolean; //default false
  isBlockedFromSharing: boolean; //default false
  requireRealNames: boolean; // default true
  isContentFilterEnabled?: boolean; // default true
  language?: string;
  clonedFromPresentationId?: string;
  clonedFromTenantId?: string;
  activeLivePresentations?: ById<ActiveGame>;
  /* eslint-disable  @typescript-eslint/no-explicit-any */
  metadata: any; //TODO: fix this
  settings?: TemplateSettings | null;
};
export type Presentation = {
  id: string;
  tenantId: string;
  createdAt: number;

  creatorId: string;
  parentId: string | null;
  isPrivate: boolean;
  isDeleted: boolean;
  deletedAt: number | null;
  content: PresentationContent;
  aiContent?: AiContent;
  modifiedAt?: number;
};

export type SchoolInfo = {
  id: string;
  name: string;
  address: string;
};

export type LicenseInfo = {
  validFrom: number;
  validThrough: number;
  price: number;
};

export type UsageInfo = {
  totalTeachers: number;
  totalStudents: number;
  totalLessonsCreated: number;
  totalLessonsPresented: number;
  totalReportInsightsCreated: number;
};

export type UserInsight = {
  userId: string;
  name: string;
  email: string;
  usage: UsageInfo;
};

export type Standards = {
  id: string;
  label: string;
  description: string;
  country?: string;
  region?: string;
  subject?: string;
  grade?: string;
  strand?: string;
};

export type StandardsFilterParams = {
  country?: string;
  region?: string;
  subject?: string;
  grade?: string;
};

export interface AugmentedStandards extends Standards {
  id: string;
  standardsSet: string;
  grades: string[];
  gradeLabel: string;
  subject: string;
  code: string;
  label: string;
  description: string;
}

export type PersonalPresentationContent = Omit<
  PresentationContent,
  'activeLivePresentations'
> & { activeLivePresentation?: ActiveGame };

export type PresentationDTO = {
  id: string;
  tenantId: string;
  createdAt: Date;
  modifiedAt: Date;
  creatorId: string;
  parentId: string | null;
  isPrivate: boolean;
  editCounter: number | null;
  isDeleted: boolean;
  deletedAt: Date | null;
  content: PersonalPresentationContent;
  aiContent?: AiContent;
};

export type GeneratedStudentResponse = {
  responseId: string;
  slideId: string;
  responsePrompt: string; //IDK, Very poor etc
  response: string;
};

export type DistrictUsageData = {
  createdPresentationCount: number;
  livePresentationCount: number;
  teacherCount: number;
  organizationId: string;
  organizationName: string;
};

export type SchoolUsageData = {
  createdPresentationCount: number;
  email: string;
  livePresentationCount: number;
  teacherId: string;
  teacherName: string;
};

// --- Insights (SCR/ECR dashboard) — mirrors AiAssessedResponseService.types.ts on the API.
// Shape follows the Curipod Admin Dashboard design handoff: every aggregate
// carries baseline vs current averages, per-rubric-level distribution
// percentages, and the deltaPp/pctChange pair the DeltaChip renders.

// SCR/ECR are Texas STAAR-style response lengths; GMAS is the Georgia
// Milestones ELA Extended Writing Task (0–8 raw points, bucketed into four
// performance levels). A district's active family set is chosen from the
// looked-up org's `stateCode` — see `Insights/families.ts`.
export type ScoreFamily = 'SCR' | 'ECR' | 'GMAS';

// Map keyed by lowercase rubric family ('scr', 'ecr', 'gmas', ...). Mirrors
// the backend's `ByRubric<T>` so new response slots that fan out by rubric
// pick up the new key automatically — callers fail to type-check until they
// populate it. Use this for new rubric-keyed fields; the older `{ scr, ecr }`
// shapes are migrated incrementally.
export type ByRubric<T> = { [K in Lowercase<ScoreFamily>]: T };

// Percentages 0..100, summing to ~100 per phase. Position in array maps to
// the rubric's `scale` index (e.g. SCR: [0, 1, 2] = no-credit / partial / full).
type RubricDistribution = number[];

// One day in the lesson-volume histogram that powers the time-period
// selector. `date` is ISO YYYY-MM-DD (the lesson's local date on the
// backend); `count` is how many Curipod lessons of the active rubric ran
// that day. The series spans the full data window the customer has on
// file — typically the start of the school year through today — so the
// selector can show both the active range and the surrounding data.
export type LessonActivityPoint = {
  date: string;
  count: number;
};

// Inputs for the "Your district's growth opportunity" V2 card on the
// district view. Mirror of the backend type — see
// AiAssessmentService.types.ts for the full math/explanation. Every
// number reflects the active from/to window.
export type GrowthOpportunity = {
  studentsMovedUp: number;
  studentsMovedUpPotential: number;
  classesAtVolume: number;
  classesUnderused: number;
  classesNotUsing: number;
  totalClasses: number;
  studentsUntapped: number;
  studentsUnderused: number;
  /** Roster headcount in at-volume classes — denominator for liftRatePct. */
  studentsAtVolume: number;
  /** Movers within at-volume classes only (subset of studentsMovedUp). */
  studentsMovedUpAtVolume: number;
  /**
   * studentsMovedUpAtVolume / studentsAtVolume × 100, clamped 0..100.
   * 0 when no at-volume cohort. The "at {volumeThreshold}+ lessons, X%
   * of students lift a band" line reads this directly.
   */
  liftRatePct: number;
  /**
   * District-wide enrolled-student count behind the `classesNotUsing` /
   * `studentsUntapped` estimates. The card uses this to attribute the
   * bucket-0 numbers ("based on an estimated enrollment of N students").
   * 0 when no in-scope school reported an enrollment.
   */
  enrollment: number;
  /**
   * @deprecated Use `liftRatePct` + `studentsAtVolume`. Historical: this
   * was movers-per-class, not roster-per-class, so dividing
   * `studentsMovedUp / (classesAtVolume × studentsPerClassAtVolume)`
   * silently collapsed to a nonsensical ratio.
   */
  studentsPerClassAtVolume: number;
  /** Lesson-count cut-off the backend picked for "at volume". */
  volumeThreshold: number;
};

// Baseline-to-current snapshot for any rollup (district, school, teacher,
// grade). Aggregates average across teachers in scope.
export type InsightsAggregate = {
  // # of teachers (district/school/grade) or # of lessons (teacher).
  n: number;
  // Average rubric score on the relevant baseline lesson.
  first: number;
  last: number;
  // Raw points delta (last - first).
  deltaPp: number;
  // Percentage change relative to baseline.
  pctChange: number;
  distFirst: RubricDistribution;
  distLast: RubricDistribution;
  /**
   * Distinct students contributing to the aggregate. Sourced from
   * `COUNT(DISTINCT participantId)` on the backend so a student who spans
   * multiple classes is counted once. Optional only for back-compat with
   * legacy responses — current backend always populates it.
   */
  studentCount?: number;
  /**
   * Total Curipod lessons in the window for this scope + rubric. Used by
   * the period selector's "{N} Curipod SCR lessons" badge.
   */
  lessonCount?: number;
};


type SchoolInsightRow = {
  orgId: string;
  name: string;
  grades: string | null;
  teacherCount: number;
  /**
   * Predicted class slices contributing to this row's aggregate. This is
   * the *weight* used when rolling the school up to the district number,
   * so it's surfaced in the UI to make the math reproducible.
   */
  classCount: number;
  avgFirst: number;
  avgLast: number;
  deltaPp: number;
  pctChange: number;
  distFirst: RubricDistribution;
  distLast: RubricDistribution;
};

export type TeacherInsightRow = {
  userId: string;
  name: string | null;
  email: string | null;
  // Grade level (e.g. "6th Grade") and subject — null when we don't know.
  grade: string | null;
  subject: string | null;
  lessonCount: number;
  avgFirst: number;
  avgLast: number;
  deltaPp: number;
  pctChange: number;
};

type GradeInsightRow = {
  grade: string;
  teacherCount: number;
  /** Class slices contributing to this grade — the weight used in rollups. */
  classCount: number;
  avgFirst: number;
  avgLast: number;
  deltaPp: number;
  pctChange: number;
  distFirst: RubricDistribution;
  distLast: RubricDistribution;
};

export type LessonStudentResponse = {
  responseId: string;
  // The AI-assessed slide this response belongs to. A single lesson can host
  // multiple aiAssessedAnswers slides, so the same student can appear in the
  // lesson detail list more than once — once per slide they answered.
  slideId: string | null;
  participantId: string;
  participantName: string | null;
  score: number;
  response: string | null;
  feedback: string | null;
};

export type LessonInsightRow = {
  livePresentationId: string;
  topic: string | null;
  prompt: string | null;
  // Pre-formatted date for display ("May 6, 2026"). Backend renders this.
  date: string | null;
  avg: number;
  distPct: RubricDistribution;
  responseCount: number;
  // Predicted class this lesson belongs to. Null when clustering hasn't
  // assigned a class yet (one-off PD demo, or backfill not re-run since).
  classId: string | null;
  // Admin-set name; null falls back to the derived "Class A/B/C…" label.
  className: string | null;
};

// One predicted class for a teacher in one rubric. Drives the legend on
// the teacher insights page.
type ClassInsightRow = {
  classId: string;
  name: string | null;
  // Full normalised student roster — admin sees every student in the class.
  // Stored lowercase on the backend; the frontend title-cases on display.
  canonicalRoster: string[];
  studentCount: number;
  sessions: number;
  firstSeenAt: string | null;
  lastSeenAt: string | null;
  avgFirst: number;
  avgLast: number;
  deltaPp: number;
  pctChange: number;
  distFirst: RubricDistribution;
  distLast: RubricDistribution;
};

export type DistrictInsightsResult = {
  orgId: string;
  name: string;
  short: string | null;
  location: string | null;
  /**
   * 2-letter US state of the looked-up org (e.g. 'GA'), upper-cased —
   * mirrors `GapsOverviewResult.stateCode`. Drives which score families the
   * dashboard shows (GA → GMAS instead of SCR/ECR). Optional for back-compat
   * with responses predating the field; absent → default SCR/ECR families.
   */
  stateCode?: string | null;
  // Curipod customer (full access) vs prospect (SCR-only, ECR locked).
  customer: boolean;
  scr: InsightsAggregate | null;
  ecr: InsightsAggregate | null;
  gmas: InsightsAggregate | null;
  /** Distinct teacher + class counts per family for hero stats. */
  teacherCount: ByRubric<number>;
  classCount: ByRubric<number>;
  schools: ByRubric<SchoolInsightRow[]>;
  grades: ByRubric<GradeInsightRow[]>;
  /**
   * Per-rubric "optimal lesson volume" buckets, one row per
   * lessons-per-class count (1, 2, 3, 4, 5+). Computed by the backend
   * directly from class slices — no client-side aggregation.
   *
   * `minBucketClasses` is the trust threshold: buckets with fewer than
   * this many contributing classes shouldn't anchor recommendations.
   * Shipped from the backend so the chart and the peak-detection math
   * use the exact same floor.
   */
  /** Each rubric-keyed value has length 6 — lessonsPerClass 0, 1, 2, 3, 4, 5+. */
  lessonGrowthBuckets: ByRubric<LessonGrowthBucket[]> & {
    minBucketClasses: number;
    /**
     * The bucket the backend picked as the data-driven volume threshold:
     * highest `avgPctChange` among buckets with
     * `classCount ≥ minBucketClasses` (bucket-0 and bucket-1 excluded).
     * Null when no bucket qualifies and the fallback threshold is in
     * play. When non-null, `optimalBucket[family].lessonsPerClass`
     * equals `growthOpportunity[family].volumeThreshold`.
     */
    optimalBucket: ByRubric<LessonGrowthBucket | null>;
  };
  /**
   * Daily lesson volume across the org's full data window (school year
   * cutoff to today), one series per rubric. Drives the histogram in the
   * time-period selector — the active range is highlighted, the rest
   * forms the surrounding context bars. Sparse — only days with at
   * least one lesson are returned; the selector fills zero-volume days
   * between bars itself.
   */
  lessonActivity: Record<ScoreFamily, LessonActivityPoint[]>;
  /**
   * Per-rubric "growth opportunity" — drives the gap-bar card on the
   * district view. Adapts to the active from/to window.
   */
  growthOpportunity: ByRubric<GrowthOpportunity>;
};

export type LessonGrowthBucket = {
  /**
   * Lessons-per-class for this bucket. 0 means "haven't started" (the
   * `classesNotUsing` cohort); 1..4 map directly; 5 is the "5+" tail.
   */
  lessonsPerClass: number;
  /** Number of classes that contributed to this bucket. */
  classCount: number;
  /**
   * Roster headcount in this bucket. For bucket-0 this is the
   * `studentsUntapped` estimate (`enrollment − studentsUsing`).
   */
  studentCount: number;
  /**
   * Estimated students in this bucket who crossed a rubric boundary
   * upward (per-class distribution shift × class studentCount, summed).
   * 0 for bucket-0 and bucket-1 — neither has a measurable first→last
   * span.
   */
  studentsLiftedBand: number;
  /** Mean percent change (last vs first lesson) across the contributing classes. */
  avgPctChange: number;
  /** Mean rubric-point delta across the contributing classes. */
  avgDeltaPp: number;
  /**
   * `studentsLiftedBand / studentCount × 100`, clamped 0..100. The
   * percent of students in this bucket who lifted a band.
   */
  liftRatePct: number;
};

export type SchoolInsightsResult = {
  orgId: string;
  name: string;
  grades: string | null;
  parentOrgId: string | null;
  parentName: string | null;
  customer: boolean;
  /** Looked-up org's 2-letter state (upper-cased). See DistrictInsightsResult. */
  stateCode?: string | null;
  rubric: ScoreFamily;
  aggregate: InsightsAggregate;
  /**
   * Distinct teacher count for this school in this rubric. The hero shows
   * this — `aggregate.n` is the class-slice count used for the math.
   */
  teacherCount: number;
  /** Predicted-class count contributing to the aggregate. */
  classCount: number;
  teachers: TeacherInsightRow[];
  gradeRollup: GradeInsightRow[];
  /**
   * Daily lesson volume for this school, one series per rubric. Same
   * shape as the district view — drives the histogram in the time-period
   * selector. Optional for back-compat with old API responses; once the
   * matching backend ships everywhere this can drop the `?`.
   */
  lessonActivity?: Record<ScoreFamily, LessonActivityPoint[]>;
};

export type TeacherInsightsResult = {
  userId: string;
  name: string | null;
  grade: string | null;
  subject: string | null;
  schoolOrgId: string | null;
  schoolName: string | null;
  parentOrgId: string | null;
  /** Looked-up org's 2-letter state (upper-cased). See DistrictInsightsResult. */
  stateCode?: string | null;
  rubric: ScoreFamily;
  aggregate: InsightsAggregate;
  lessons: LessonInsightRow[];
  classes: ClassInsightRow[];
  /** Daily lesson volume scoped to this teacher. See SchoolInsightsResult. */
  lessonActivity?: Record<ScoreFamily, LessonActivityPoint[]>;
};

export type ClassInsightsResult = {
  classId: string;
  name: string | null;
  canonicalRoster: string[];
  studentCount: number;
  organizationId: string;
  schoolName: string | null;
  teacherIds: string[];
  teacherName: string | null;
  /** Looked-up org's 2-letter state (upper-cased). See DistrictInsightsResult. */
  stateCode?: string | null;
  rubric: ScoreFamily;
  aggregate: InsightsAggregate;
  lessons: LessonInsightRow[];
  /** Daily lesson volume scoped to this class. See SchoolInsightsResult. */
  lessonActivity?: Record<ScoreFamily, LessonActivityPoint[]>;
};

export type LessonInsightsResult = {
  livePresentationId: string;
  topic: string | null;
  prompt: string | null;
  date: string | null;
  avg: number;
  distPct: RubricDistribution;
  /** Looked-up org's 2-letter state (upper-cased). See DistrictInsightsResult. */
  stateCode?: string | null;
  rubric: ScoreFamily;
  teacherName: string | null;
  schoolName: string | null;
  responses: LessonStudentResponse[];
};

export type InsightsQueryArgs = {
  orgId: string;
  family?: ScoreFamily;
  // ISO date strings, inclusive of `from`, exclusive of `to`.
  from?: string;
  to?: string;
};

// One row in the internal state-growth ranking. Mirrors the backend's
// StateGrowthOrg in src/coreAPI/api/services/AiAssessmentService/
// AiAssessmentService.types.ts — keep in sync if either side changes.
export type StateGrowthOrg = {
  orgId: string;
  type: 'District' | 'School';
  name: string;
  city: string | null;
  region: string | null;
  parentOrgId: string | null;
  parentName: string | null;
  customer: boolean;
  teacherCount: { scr: number; ecr: number };
  classCount: { scr: number; ecr: number };
  scr: InsightsAggregate | null;
  ecr: InsightsAggregate | null;
};

export type StateGrowthResult = {
  region: string;
  country: string;
  generatedAt: string;
  orgs: StateGrowthOrg[];
};

export enum SupportedIntegrationLabel {
  GoogleDrive = 'google-drive',
}

export type SupportedIntegrationResponse = {
  authorizationUrl: string;
  accessToken?: { value: string; expiresAt: Date };
};

export type AccountType =
  | 'teacherPrimarySchool'
  | 'teacherMiddleSchool'
  | 'teacherHighSchool'
  | 'studentPrimarySchool'
  | 'studentMiddleSchool'
  | 'studentHighSchool'
  | 'higherEducation'
  | 'professional'
  | 'personal';

export type SchoolRoleType =
  | 'Teacher'
  | 'Instructional/Tech Coach'
  | 'Library Media Specialist'
  | 'School Administrator'
  | 'District Administrator'
  | 'Purchasing Manager'
  | 'Student'
  | 'Other';

export type SubjectType =
  | 'English Language Arts'
  | 'English'
  | 'Literacy'
  | 'Math'
  | 'Science'
  | 'Social Studies'
  | 'Other';

export type GradeType =
  | 'Kindergarten'
  | '1st'
  | '2nd'
  | '3rd'
  | '4th'
  | '5th'
  | '6th'
  | '7th'
  | '8th'
  | '9th'
  | '10th'
  | '11th'
  | '12th'
  | '13th'
  | 'PD'
  | 'Higher Education'
  | 'Other';

export type IntendedUseType =
  | 'Create Excitement for Reading and Writing'
  | 'Give Immediate Feedback on Student Writing'
  | 'Prepare for Testing'
  | 'Create Interactive Presentations Using AI'
  | 'Other';

export type SignUpOriginType =
  | 'Social Media'
  | 'Professional Development Session'
  | 'Coworker Referral'
  | 'Conference Presentation'
  | 'Other';

export type DatabaseSchool = {
  schoolId: number;
  schoolName: string;
  city?: string;
  country: string;
  districtId?: number;
  districtName?: string;
  state?: string;
  zipCode?: string;
};

// this comes from the api repo: src/coreAPI/api/services/GoogleDriveService/GoogleDriveApiService.ts
export type PreferredMimeType = 'PPTX' | 'PDF';

export type NewPresentationMetadata = {
  selectedCardId?: string;
  isTestPrep?: boolean;
  subject?: string;
}